import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Form, Input, List, Modal, Radio, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import "./LearnerQuizzes.css";

const { Text } = Typography;

function visibleQuiz(quiz) {
  return {
    id: quiz.id,
    title: quiz.title,
    maxAttempts: quiz.maxAttempts,
    attemptsUsed: quiz.attemptsUsed,
    attemptsRemaining: quiz.attemptsRemaining,
    canAttempt: quiz.canAttempt,
    canAttemptReason: quiz.canAttemptReason,
    myLastScore: quiz.myLastScore,
    myLastStatus: quiz.myLastStatus,
    timeLimitMinutes: quiz.timeLimitMinutes,
    isAutoMarked: quiz.isAutoMarked,
    questions: (quiz.questions || []).map((question) => ({
      id: question.id,
      questionText: question.questionText,
      questionType: question.questionType,
      options: question.options || [],
      marks: question.marks,
      order: question.order,
    })),
  };
}

export default function LearnerQuizzes({ api, contentID, studentID, onAttempt }) {
  const [form] = Form.useForm();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attemptsByQuiz, setAttemptsByQuiz] = useState({});
  const [attemptsLoading, setAttemptsLoading] = useState({});
  const [attemptsErrors, setAttemptsErrors] = useState({});
  const [refreshQuizzes, setRefreshQuizzes] = useState(0);
  const [draft, setDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const answersRef = useRef({});
  const attemptRef = useRef(null);
  const saveQueue = useRef(Promise.resolve());
  const autoSaveTimer = useRef(null);
  const contextVersion = useRef(0);
  const deadlineRef = useRef(null);

  useEffect(() => () => {
    contextVersion.current += 1;
    clearTimeout(autoSaveTimer.current);
  }, [contentID, studentID]);

  useEffect(() => {
    setResult(null);
  }, [contentID, studentID]);

  useEffect(() => {
    let current = true;
    setSubmitting(false);
    setConfirmSubmit(false);
    setQuizzes([]);
    setActiveQuiz(null);
    setDraft(null);
    attemptRef.current = null;
    answersRef.current = {};
    setAttemptsByQuiz({});
    setAttemptsLoading({});
    setAttemptsErrors({});
    setError("");
    if (!contentID) return () => { current = false; };

    setLoading(true);
    async function loadQuizzes() {
      const found = [];
      let page = 1;
      while (current) {
        const response = await api.quizzes.list({
          content: contentID,
          ...(studentID ? { studentID } : {}),
          page,
          pageSize: 100,
        });
        if (!current) return;
        if (!response?.ok) {
          setError(getLmsErrorMessage(response, "Unable to load quizzes"));
          setLoading(false);
          return;
        }
        const result = getLmsListItems(response.data);
        found.push(...result.items);
        if (!result.next) break;
        page += 1;
      }
      if (current) {
        setQuizzes(found.map(visibleQuiz));
        setLoading(false);
      }
    }
    loadQuizzes();
    return () => { current = false; };
  }, [api, contentID, studentID, refreshQuizzes]);

  const questions = useMemo(
    () => [...(activeQuiz?.questions || [])].sort((a, b) => a.order - b.order),
    [activeQuiz],
  );

  const pendingKey = (quizID, attemptID) => `lms-pending-answers:${studentID}:${quizID}:${attemptID}`;
  const rememberPending = (quizID, attemptID, answers) => {
    if (!attemptID) return;
    try { sessionStorage.setItem(pendingKey(quizID, attemptID), JSON.stringify({ answers, draftID: attemptID, savedAt: Date.now() })); } catch { /* Saving to the server still works if browser storage is unavailable. */ }
  };
  const forgetPending = (quizID, attemptID) => {
    try { sessionStorage.removeItem(pendingKey(quizID, attemptID)); } catch { /* Browser storage may be unavailable. */ }
  };

  const startQuiz = async (quiz) => {
    if (!studentID || !quiz.questions.length || submitting) return;
    clearTimeout(autoSaveTimer.current);
    const version = ++contextVersion.current;
    attemptRef.current = null;
    answersRef.current = {};
    setDraft(null);
    setSubmitting(true);
    const response = await api.quizzes.resume(quiz.id, { studentID });
    if (version !== contextVersion.current) return;
    if (!response?.ok) {
      setSubmitting(false);
      message.error(getLmsErrorMessage(response, "Unable to restore quiz"));
      return;
    }
    const candidate = response.data?.draft;
    const restored = candidate && ["Saved", "In Progress"].includes(candidate.status || "Saved") && !candidate.submittedAt ? candidate : null;
    if (!restored && response.data?.attemptsRemaining === 0) {
      setSubmitting(false);
      setResult(response.data.lastSubmitted);
      setRefreshQuizzes((value) => value + 1);
      return;
    }
    // Allocate the new attempt before accepting answers or scheduling autosaves.
    let currentAttempt = restored;
    if (!currentAttempt) {
      const created = await api.quizzes.attempt(quiz.id, { quizID: quiz.id, studentID, answers: {} });
      if (version !== contextVersion.current) return;
      if (!created?.ok || !created.data?.id || !["Saved", "In Progress"].includes(created.data.status || "Saved")) {
        setSubmitting(false);
        message.error(getLmsErrorMessage(created, "Unable to start a new attempt"));
        return;
      }
      currentAttempt = created.data;
    }
    form.resetFields();
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(pendingKey(quiz.id, currentAttempt.id)) || "null");
      // Preserve old recovery data only when it identifies this exact open attempt.
      const legacyKey = `lms-pending-answers:${studentID}:${quiz.id}`;
      const legacy = JSON.parse(sessionStorage.getItem(legacyKey) || "null");
      if (!pending && restored && String(legacy?.draftID) === String(currentAttempt.id)) pending = legacy;
      sessionStorage.removeItem(legacyKey);
    } catch { /* Ignore unavailable or invalid storage. */ }
    const hasPending = pending?.answers && String(pending.draftID) === String(currentAttempt.id) &&
      (!currentAttempt.updated_at || pending.savedAt > Date.parse(currentAttempt.updated_at));
    const savedAnswers = { ...(currentAttempt.answers || {}), ...(hasPending ? pending.answers : {}) };
    // setFieldsValue merges maps: explicitly clear every unanswered question.
    answersRef.current = Object.fromEntries(quiz.questions.map((question) => [String(question.id), savedAnswers[String(question.id)] ?? null]));
    form.setFieldsValue({ answers: answersRef.current });
    attemptRef.current = currentAttempt;
    setDraft(currentAttempt);
    deadlineRef.current = currentAttempt.expiresAt ? Date.parse(currentAttempt.expiresAt) : null;
    setRemaining(currentAttempt.remainingSeconds ?? null);
    setResult(null);
    setSaveStatus(restored ? "Saved answers restored" : "");
    setActiveQuiz(quiz);
    setSubmitting(false);
    if (hasPending) {
      rememberPending(quiz.id, currentAttempt.id, answersRef.current);
      await saveDraft(quiz, answersRef.current, version);
    }
  };

  const saveDraft = (quiz = activeQuiz, answers = answersRef.current, version = contextVersion.current) => {
    const snapshot = { ...answers };
    const attemptID = attemptRef.current?.id;
    if (!quiz || !attemptID) return Promise.resolve(null);
    const operation = saveQueue.current.catch(() => null).then(async () => {
      if (version !== contextVersion.current || attemptID !== attemptRef.current?.id) return null;
      setSaveStatus("Saving…");
      const response = await api.quizzes.attempt(quiz.id, { quizID: quiz.id, studentID, attemptID, answers: snapshot });
      if (version !== contextVersion.current || attemptID !== attemptRef.current?.id) return null;
      if (!response?.ok) {
        setSaveStatus(getLmsErrorMessage(response, "Save failed. Your answers remain here; retry when connected."));
        return null;
      }
      if (String(response.data?.id) !== String(attemptID)) {
        setSaveStatus("Save failed: the server returned a different attempt.");
        return null;
      }
      if (!["Saved", "In Progress"].includes(response.data?.status || "Saved")) {
        forgetPending(quiz.id, attemptID);
        setResult(response.data);
        closeQuiz();
        onAttempt?.();
        setRefreshQuizzes((current) => current + 1);
        return null;
      }
      attemptRef.current = response.data;
      setDraft(response.data);
      deadlineRef.current = response.data.expiresAt ? Date.parse(response.data.expiresAt) : null;
      setRemaining(response.data.remainingSeconds ?? null);
      setSaveStatus("Saved");
      // A newer keystroke may have arrived while this snapshot was saving.
      if (JSON.stringify(snapshot) === JSON.stringify(answersRef.current)) forgetPending(quiz.id, attemptID);
      return response.data;
    });
    saveQueue.current = operation;
    return operation;
  };

  const closeQuiz = () => {
    clearTimeout(autoSaveTimer.current);
    contextVersion.current += 1;
    setActiveQuiz(null);
    setSubmitting(false);
    setDraft(null);
    attemptRef.current = null;
    answersRef.current = {};
    setRemaining(null);
    setSaveStatus("");
    setConfirmSubmit(false);
    deadlineRef.current = null;
    form.resetFields();
  };

  const saveAndExit = async () => {
    const version = contextVersion.current;
    setSubmitting(true);
    clearTimeout(autoSaveTimer.current);
    const saved = await saveDraft();
    if (version !== contextVersion.current) return;
    setSubmitting(false);
    if (saved) closeQuiz();
  };

  const answersChanged = (_, values) => {
    const answers = {};
    for (const question of questions) {
      const value = values.answers?.[question.id];
      answers[String(question.id)] = typeof value === "string" && value.trim() ? value.trim() : null;
    }
    answersRef.current = answers;
    rememberPending(activeQuiz.id, attemptRef.current?.id, answers);
    setSaveStatus("Unsaved changes");
    clearTimeout(autoSaveTimer.current);
    const version = contextVersion.current;
    autoSaveTimer.current = setTimeout(() => saveDraft(activeQuiz, answers, version), 800);
  };

  const loadAttempts = async (quizID, page = 1) => {
    if (!studentID || attemptsLoading[quizID]) return;
    setAttemptsLoading((current) => ({ ...current, [quizID]: true }));
    setAttemptsErrors((current) => ({ ...current, [quizID]: "" }));
    const response = await api.quizzes.attempts(quizID, { student: studentID, page, pageSize: 10 });
    setAttemptsLoading((current) => ({ ...current, [quizID]: false }));
    if (!response?.ok) {
      setAttemptsErrors((current) => ({ ...current, [quizID]: getLmsErrorMessage(response, "Unable to load attempts") }));
      return;
    }
    setAttemptsByQuiz((current) => ({ ...current, [quizID]: { ...getLmsListItems(response.data), page } }));
  };

  const submitQuiz = async () => {
    if (!activeQuiz || !studentID || submitting) return;
    setSubmitting(true);
    const version = contextVersion.current;
    clearTimeout(autoSaveTimer.current);
    const saved = await saveDraft();
    if (version !== contextVersion.current) return;
    if (!saved) { setSubmitting(false); setConfirmSubmit(false); return; }
    const response = await api.quizAttempts.submit(saved.id);
    if (version !== contextVersion.current) return;
    setSubmitting(false);
    if (!response?.ok) {
      setConfirmSubmit(false);
      message.error(getLmsErrorMessage(response, "Unable to submit quiz. Retry submission when connected."));
      return;
    }
    forgetPending(activeQuiz.id, saved.id);
    setResult(response.data);
    closeQuiz();
    onAttempt?.();
    setRefreshQuizzes((current) => current + 1);
  };

  useEffect(() => {
    if (!activeQuiz) return undefined;
    const timer = setInterval(() => {
      if (!deadlineRef.current) return;
      const seconds = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setRemaining(seconds);
      if (seconds === 0 && !submitting) submitQuiz();
    }, 1000);
    return () => clearInterval(timer);
    // Read the latest draft and submitting state on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuiz, submitting]);

  useEffect(() => {
    if (!activeQuiz) return undefined;
    const retry = () => saveDraft();
    const timer = setInterval(retry, 15000);
    window.addEventListener("online", retry);
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => { clearInterval(timer); window.removeEventListener("online", retry); window.removeEventListener("beforeunload", warn); };
    // The draft is retained in memory and retried after reconnecting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuiz]);

  return (
    <Card
      size="small"
      title="Quizzes"
      style={{ marginTop: 16 }}
      extra={<Tag icon={<CheckCircleOutlined />}>{quizzes.length} available</Tag>}
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {result && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${result.quizTitle || "Quiz"} submitted`}
          description={result.score == null
            ? `Attempt ${result.attemptNumber} is awaiting marking.`
            : `Attempt ${result.attemptNumber}: ${result.score}%`}
        />
      )}
      {loading ? <Spin /> : (
        <List
          dataSource={quizzes}
          locale={{ emptyText: <Empty description="No quizzes for this content" /> }}
          renderItem={(quiz) => (
            <List.Item key={quiz.id} className="learner-quiz">
              <div className="learner-quiz-summary">
                <div className="learner-quiz-details">
                  <List.Item.Meta
                    title={quiz.title}
                    description={[
                      `${quiz.questions.length} question${quiz.questions.length === 1 ? "" : "s"}`,
                      quiz.timeLimitMinutes ? `${quiz.timeLimitMinutes} min time limit` : null,
                      quiz.attemptsRemaining != null ? `${quiz.attemptsRemaining} attempt${quiz.attemptsRemaining === 1 ? "" : "s"} remaining` : null,
                      quiz.myLastScore != null ? `Last score: ${quiz.myLastScore}%` : null,
                      quiz.myLastStatus ? `Last attempt: ${quiz.myLastStatus}` : null,
                    ].filter(Boolean).join(" · ")}
                  />
                  {(!studentID || quiz.canAttempt !== true || !quiz.questions.length) && <Text type="secondary">{!studentID ? "Student profile unavailable" : !quiz.questions.length ? "No questions available" : quiz.canAttemptReason || "Attempt unavailable"}</Text>}
                </div>
                <Space wrap className="learner-quiz-actions">
                  {studentID && quiz.canAttempt === true && quiz.questions.length > 0 && <Button type="primary" icon={<PlayCircleOutlined />} loading={submitting} onClick={() => startQuiz(quiz)}>
                    Attempt quiz
                  </Button>}
                  <Button icon={<ClockCircleOutlined />} onClick={() => loadAttempts(quiz.id)} disabled={!studentID}>
                    View attempts
                  </Button>
                </Space>
              </div>
              {attemptsErrors[quiz.id] && <Alert type="error" showIcon message={attemptsErrors[quiz.id]} style={{ marginTop: 8 }} />}
              {attemptsLoading[quiz.id] && <Spin size="small" />}
              {attemptsByQuiz[quiz.id] && (
                <List
                  className="learner-quiz-history"
                  size="small"
                  header="Your attempts"
                  dataSource={attemptsByQuiz[quiz.id].items}
                  pagination={attemptsByQuiz[quiz.id].count > 10 ? {
                    current: attemptsByQuiz[quiz.id].page,
                    pageSize: 10,
                    total: attemptsByQuiz[quiz.id].count,
                    onChange: (page) => loadAttempts(quiz.id, page),
                  } : false}
                  locale={{ emptyText: "No attempts yet" }}
                  renderItem={(attempt) => (
                    <List.Item>
                      <Space wrap>
                        <Text>Attempt {attempt.attemptNumber}</Text>
                        <Tag>{attempt.statusDisplay || attempt.status}</Tag>
                        {attempt.score != null && <Text>{attempt.score}%</Text>}
                        {attempt.submittedAt && <Text type="secondary">{new Date(attempt.submittedAt).toLocaleString()}</Text>}
                        {attempt.teacherFeedback && <Text>{attempt.teacherFeedback}</Text>}
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </List.Item>
          )}
        />
      )}

      <Modal
        title={activeQuiz?.title || "Attempt quiz"}
        open={Boolean(activeQuiz)}
        onCancel={() => { if (!submitting) saveAndExit(); }}
        footer={[
          <Button key="save" disabled={submitting} onClick={() => saveDraft()}>Save &amp; Continue</Button>,
          <Button key="exit" loading={submitting} onClick={saveAndExit}>Save &amp; Exit</Button>,
          <Button key="submit" type="primary" disabled={submitting} onClick={() => setConfirmSubmit(true)}>Submit</Button>,
        ]}
        closable={!submitting}
        maskClosable={!submitting}
        width={760}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">{questions.length} questions. You can leave a question unanswered.</Text>
          {remaining != null && <Tag icon={<ClockCircleOutlined />} color={remaining < 60 ? "red" : "blue"}>{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")} remaining</Tag>}
          {saveStatus && <Alert type={saveStatus.includes("failed") ? "error" : "info"} message={saveStatus} showIcon />}
          {draft && <Text type="secondary">Draft attempt {draft.attemptNumber}</Text>}
          <Form form={form} layout="vertical" preserve={false} onValuesChange={answersChanged} disabled={submitting || remaining === 0}>
            {questions.map((question, index) => (
              <Card key={question.id} size="small" style={{ marginBottom: 12 }}>
                <Form.Item
                  name={["answers", question.id]}
                  label={`${index + 1}. ${question.questionText} (${question.marks || 1} mark${question.marks === 1 ? "" : "s"})`}
                >
                  {question.questionType === "MCQ" ? (
                    <Radio.Group options={question.options.map((option, index) => ({ label: `${String.fromCharCode(65 + index)}. ${option}`, value: option }))} style={{ display: "grid", gap: 8 }} />
                  ) : question.questionType === "True/False" ? (
                    <Radio.Group options={["True", "False"]} />
                  ) : (
                    <Input.TextArea rows={3} placeholder="Write your answer" />
                  )}
                </Form.Item>
              </Card>
            ))}
          </Form>
        </Space>
      </Modal>
      <Modal title="Submit this quiz?" open={confirmSubmit} onCancel={() => { if (!submitting) setConfirmSubmit(false); }} onOk={submitQuiz} confirmLoading={submitting} okText="Submit quiz" cancelButtonProps={{ disabled: submitting }} closable={!submitting} maskClosable={!submitting}>
        <Text>Your saved answers will be submitted for marking. You cannot change them after submission.</Text>
      </Modal>
    </Card>
  );
}
